import type { Product } from '../types/pipeline';
import type { GpcPriceQuote } from '../api/gpcFunctions';

/**
 * Build pre-seed Competition Quote rows (stage 805, Path B) from the products
 * the cardholder SELECTED on the Mandatory Source stage (804).
 *
 * For each selected product we map the real eLibrary seller, the GSA Schedule /
 * contract number, the price, and the evidence URL into a `GpcPriceQuote` with
 * `from_search: true`. Rows are de-duplicated by `(vendor_name, source, price)`
 * and sorted by price ascending (lowest first); priced rows precede unpriced.
 *
 * An empty selection yields an empty array, so the quotes stage falls back to
 * its existing blank-row behaviour.
 */

const coercePrice = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  // Product.price is typed as a string (computed from award_amount); strip any
  // currency formatting before coercing.
  const cleaned = String(value).replace(/[^0-9.\-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
};

const seedQuoteFor = (product: Product): GpcPriceQuote => {
  const vendor_name = product.vendor || product.manufacturer || '';
  const source =
    product.contract_number ||
    (product.specifications?.contract_number as string | undefined) ||
    product.source_type ||
    product.source ||
    '';
  const url = product.evidence?.url || product.evidence?.product_url || '';
  return {
    source,
    vendor_name,
    price: coercePrice(product.price),
    url: url || null,
    is_small_disadvantaged_business: false,
    on_sam_gov: null,
    from_search: true,
    captured_at: null,
  };
};

export function buildSeedQuotes(
  products: Product[],
  selectedIds: string[],
): GpcPriceQuote[] {
  if (!Array.isArray(products) || !Array.isArray(selectedIds) || selectedIds.length === 0) {
    return [];
  }
  const selected = new Set(selectedIds.map(String));
  // GSA products have no stable `id`, so GPCMandatorySource derives the selection
  // id as `p.id ?? gpc-product-${index}`. Match that SAME derivation here —
  // filtering by `p.id` alone yields `"undefined"` and misses every GSA row.
  const seeds = products
    .filter((p, i) => p && selected.has(String(p.id ?? `gpc-product-${i}`)))
    .map(seedQuoteFor);

  // Dedupe by (vendor_name, source, price).
  const seen = new Set<string>();
  const deduped: GpcPriceQuote[] = [];
  for (const q of seeds) {
    const key = `${q.vendor_name} ${q.source} ${q.price ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(q);
  }

  // Sort by price ascending; unpriced rows last.
  return deduped.sort((a, b) => {
    if (a.price === null && b.price === null) return 0;
    if (a.price === null) return 1;
    if (b.price === null) return -1;
    return a.price - b.price;
  });
}

/**
 * Stable identity key for a seeded Competition Quote row that intentionally
 * EXCLUDES price. Price is the field a cardholder edits most, so it must not
 * count toward "did the Mandatory Source selection change?". Two seeded rows
 * are considered the same selection iff their (vendor_name, source) match
 * (case-insensitive, trimmed).
 */
const seedIdentityKey = (q: GpcPriceQuote): string =>
  `${(q.vendor_name ?? '').trim().toLowerCase()}␟${(q.source ?? '').trim().toLowerCase()}`;

const sameKeySet = (a: Set<string>, b: Set<string>): boolean => {
  if (a.size !== b.size) return false;
  let equal = true;
  a.forEach((k) => {
    if (!b.has(k)) equal = false;
  });
  return equal;
};

/**
 * Reconcile the PERSISTED Competition Quotes against the CURRENT Mandatory
 * Source selection, so that changing the selection re-seeds the auto rows while
 * a plain reload preserves the persisted quotes (including user-typed prices on
 * seeded rows).
 *
 * Behaviour (Option 1):
 *  - `persisted` empty / not an array → return the fresh seed (first visit).
 *  - Selection UNCHANGED (the set of (vendor,source) identity keys of the
 *    persisted auto-seeded rows equals that of a fresh seed) → return
 *    `persisted` unchanged, preserving edits and ordering. A price-only edit on
 *    a seeded row does NOT change the identity set, so it is correctly treated
 *    as "unchanged".
 *  - Selection CHANGED → return `[...freshSeed, ...manualRows]` where manual
 *    rows are the persisted `from_search:false` entries (kept, appended after
 *    the refreshed seed).
 *
 * Known minor edge (acceptable for now): if a user DELETES seeded rows but keeps
 * the same selection, a reload re-adds them, because the persisted identity set
 * then no longer matches the fresh seed and we re-seed.
 */
export function reconcileSeededQuotes(
  persisted: GpcPriceQuote[],
  products: Product[],
  selectedIds: string[],
): GpcPriceQuote[] {
  const freshSeed = buildSeedQuotes(products, selectedIds);

  if (!Array.isArray(persisted) || persisted.length === 0) {
    return freshSeed;
  }

  const persistedSeedKeys = new Set(
    persisted.filter((q) => q.from_search).map(seedIdentityKey),
  );
  const freshKeys = new Set(freshSeed.map(seedIdentityKey));

  // Selection unchanged → preserve persisted exactly (edits + order intact).
  if (sameKeySet(persistedSeedKeys, freshKeys)) {
    return persisted;
  }

  // Selection changed → refreshed seed first, manual (user-added) rows appended.
  return [...freshSeed, ...persisted.filter((q) => !q.from_search)];
}
